// src/features/secret/EngineSidebar.tsx
// Left-panel engine list — the primary navigation for the secrets console. Mount is a dialog;
// selecting an engine is the only other action here (tune/unmount live on the engine header).

import { useEffect, useState, useCallback } from 'react';
import { useI18n, useSecretSecretEngine } from '@react/hooks';
import { Button, Input, ErrorAlert } from '@react/components';
import { Select } from '@/components/Select';
import { StatusBadge } from '@/components/StatusBadge';
import { FormDialog } from '@/components/form';
import type { SecretEngine, SecretEngineType } from '@core/types/secret';
import { SecretEngineTypeValues } from '@core/types/secret';
import { engineTypeLabelKey, engineStatusVariant, engineStatusLabelKey, validateMountForm } from './logic';

const MOUNTABLE_TYPES = SecretEngineTypeValues.filter((v) => v !== 'SECRET_ENGINE_TYPE_UNSPECIFIED');

// Keys / IDs render in mono (docs/ux/14-forms.md). The generated Input/Textarea components pin
// their own font-family in CSS, so an inline style (spread through the wrapper) is what wins.
const MONO_STYLE = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' } as const;

function MountEngineDialog({ open, onMounted, onClose }: { open: boolean; onMounted: (engine: SecretEngine) => void; onClose: () => void }) {
  const { t } = useI18n();
  const store = useSecretSecretEngine();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<SecretEngineType>('SECRET_ENGINE_TYPE_KV_V2');
  const [mountPath, setMountPath] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(null);

  const dirty = !!(name.trim() || slug.trim() || description.trim() || mountPath.trim());

  const reset = () => {
    setName('');
    setSlug('');
    setDescription('');
    setType('SECRET_ENGINE_TYPE_KV_V2');
    setMountPath('');
    setErrors({});
    setSubmitError(null);
  };

  const close = () => { reset(); onClose(); };

  const handleSlugChange = (v: string) => {
    setSlug(v);
    if (!mountPath || mountPath === `${slug}/`) setMountPath(v ? `${v}/` : '');
  };

  const submit = async () => {
    const validationErrors = validateMountForm(slug, mountPath);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const resp = await store.MountEngine({
        engine: {
          name: name.trim() || slug,
          slug: slug.trim(),
          description: description.trim(),
          type,
          mount_path: mountPath.trim(),
        } as SecretEngine,
      });
      if (resp.engine) {
        reset();
        onMounted(resp.engine);
      }
    } catch (err) {
      setSubmitError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={close}
      onSubmit={submit}
      submitLabel={t('secret.mount.submit')}
      canSubmit={!!slug.trim() && !!mountPath.trim()}
      isSubmitting={isSubmitting}
      dirty={dirty}
      aria-label={t('secret.mount.dialogTitle')}
    >
      <form onSubmit={(e) => { e.preventDefault(); submit(); }} noValidate className="space-y-3 px-3.5 pb-4 pt-2">
        {submitError != null && <ErrorAlert error={submitError} dismissible onDismiss={() => setSubmitError(null)} />}
        <Select
          label={t('secret.mount.field.type')}
          value={type}
          onChange={(e) => setType(e.target.value as SecretEngineType)}
          options={MOUNTABLE_TYPES.map((v) => ({ value: v, label: t(engineTypeLabelKey(v)) }))}
        />
        <Input
          label={t('secret.mount.field.slug')}
          value={slug}
          onChange={(e) => handleSlugChange(e.target.value)}
          placeholder="app-secrets"
          error={errors.slug ? t(errors.slug) : undefined}
          data-autofocus
          style={MONO_STYLE}
        />
        <Input label={t('secret.mount.field.name')} value={name} onChange={(e) => setName(e.target.value)} placeholder={t('secret.mount.field.namePlaceholder')} />
        <Input
          label={t('secret.mount.field.mountPath')}
          value={mountPath}
          onChange={(e) => setMountPath(e.target.value)}
          placeholder="app-secrets/"
          error={errors.mountPath ? t(errors.mountPath) : undefined}
          style={MONO_STYLE}
        />
        <Input label={t('secret.mount.field.description')} value={description} onChange={(e) => setDescription(e.target.value)} />
      </form>
    </FormDialog>
  );
}

export interface EngineSidebarProps {
  selectedEngineId: string | null;
  onSelect: (engine: SecretEngine) => void;
  /** Bumped by the parent to force a reload (e.g. after unmount). */
  reloadToken?: number;
}

export function EngineSidebar({ selectedEngineId, onSelect, reloadToken }: EngineSidebarProps) {
  const { t } = useI18n();
  const store = useSecretSecretEngine();
  const [engines, setEngines] = useState<SecretEngine[]>([]);
  const [loading, setLoading] = useState(true);
  const [showMount, setShowMount] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    store.ListEngines()
      .then((resp) => setEngines(resp.engines ?? []))
      .catch(() => setEngines([]))
      .finally(() => setLoading(false));
  }, [store]);

  useEffect(() => { load(); }, [load, reloadToken]);

  return (
    <aside className="w-72 flex-shrink-0 border-r border-border flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <h2 className="text-sm font-semibold text-foreground">{t('secret.sidebar.title')}</h2>
        <Button size="sm" variant="secondary" onClick={() => setShowMount(true)}>
          {t('secret.mount.button')}
        </Button>
      </div>

      <MountEngineDialog
        open={showMount}
        onMounted={(engine) => { setShowMount(false); load(); onSelect(engine); }}
        onClose={() => setShowMount(false)}
      />

      {store.error != null && <div className="p-3"><ErrorAlert error={store.error} /></div>}

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <p className="p-4 text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : engines.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">{t('secret.sidebar.empty')}</p>
        ) : (
          <ul>
            {engines.map((engine) => {
              const id = engine.entity?.id ?? '';
              const active = id === selectedEngineId;
              return (
                <li key={id}>
                  <button
                    onClick={() => {
                      // Select immediately with the list row, then refresh with the authoritative
                      // single-engine read (GetEngine) once it resolves.
                      onSelect(engine);
                      if (id) store.GetEngine(id).then(onSelect).catch(() => {});
                    }}
                    className={`w-full text-left px-4 py-2.5 border-b border-border/50 hover:bg-muted/50 transition-colors ${active ? 'bg-muted' : ''}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-foreground truncate">{engine.name || engine.slug}</span>
                      <StatusBadge variant={engineStatusVariant(engine.status)} label={t(engineStatusLabelKey(engine.status))} size="sm" />
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{t(engineTypeLabelKey(engine.type))} · {engine.mount_path}</p>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </aside>
  );
}
